import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession, signOut } from '@aws-amplify/auth';
import { AUTH_API } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';

// Create two separate contexts
const AuthContext = createContext(null);
const AuthActionsContext = createContext(null);

// This component will be rendered inside Router context
const AuthProviderWithRouter = ({ children }) => {
  const navigate = useNavigate();
  
  // Manually trigger logout
  const logout = useCallback(async () => {
    try {
      // Clear auth state immediately to prevent redirect loops
      localStorage.removeItem('auth_user');
      
      await signOut();
      navigate('/auth?state=signIn');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if signOut fails, clear local state and redirect
      localStorage.removeItem('auth_user');
      navigate('/auth?state=signIn');
    }
  }, [navigate]);

  // Provide the auth actions context
  const authActions = {
    logout
  };

  return (
    <AuthActionsContext.Provider value={authActions}>
      {children}
    </AuthActionsContext.Provider>
  );
};

// Main Auth Provider - doesn't use router hooks
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState({
    name: 'Account User',
    email: 'user@example.com',
    role: null,
    isAuthenticated: false,
    isLoading: true
  });

  // Add a ref to track if we're already loading to prevent duplicate calls
  const isLoadingRef = useRef(false);

  // Centralized function to get auth token
  const getAuthToken = async () => {
    const session = await fetchAuthSession();
    if (!session?.tokens?.idToken) {
      throw new Error("No valid authentication token found");
    }
    return session.tokens.idToken.toString();
  };

  // Session recovery function - attempts to refresh session before giving up
  const recoverSession = async () => {
    try {
      console.log('🔄 Attempting session recovery...');
      
      // Try to refresh the session
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (session?.tokens?.idToken && session?.tokens?.accessToken) {
        console.log('✅ Session recovered successfully');
        return true;
      } else {
        console.log('❌ Session recovery failed - no valid tokens after refresh');
        return false;
      }
    } catch (error) {
      console.log('❌ Session recovery failed:', error);
      return false;
    }
  };

  // Centralized function to load user data
  const loadUserData = useCallback(async () => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;

    try {
      // First, check if there's a valid session without throwing errors
      let session = await fetchAuthSession();
      
      // If no valid session or tokens, try recovery before giving up
      if (!session?.tokens?.idToken || !session?.tokens?.accessToken) {
        console.log('No valid session found, attempting recovery...');
        const recovered = await recoverSession();
        
        if (!recovered) {
          // Recovery failed, user is not authenticated
          setUser(prev => ({ 
            ...prev, 
            isLoading: false, 
            isAuthenticated: false,
            role: null,
            name: 'Account User',
            email: 'user@example.com'
          }));
          localStorage.removeItem('auth_user');
          return;
        } else {
          // Recovery succeeded, get the new session
          session = await fetchAuthSession();
        }
      }

      // Validate that the token payload is accessible (token is not expired/invalid)
      try {
        const payload = session.tokens.accessToken.payload;
        if (!payload) {
          throw new Error('Invalid token payload');
        }
        // Note: cognito:groups may not exist for new users, that's okay
      } catch (tokenError) {
        setUser(prev => ({ 
          ...prev, 
          isLoading: false, 
          isAuthenticated: false,
          role: null,
          name: 'Account User',
          email: 'user@example.com'
        }));
        localStorage.removeItem('auth_user');
        return;
      }

      // Now try to get current user - only if we have a valid session
      const currentUser = await getCurrentUser();
      
      if (currentUser && session.tokens.idToken) {
        // Fetch additional profile data from your API
        const token = session.tokens.idToken.toString();
        const profileResponse = await fetch(`${AUTH_API}/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!profileResponse.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const profileData = await profileResponse.json();
        
        const userData = {
          name: profileData.firstName && profileData.lastName 
            ? `${profileData.firstName} ${profileData.lastName}`.trim()
            : profileData.name || currentUser.username || 'Account User',
          email: profileData.email || currentUser.email || 'user@example.com',
          role: profileData.role,
          isAuthenticated: true,
          isLoading: false,
          // Include additional profile data
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          conventions: profileData.conventions || [],
          userId: profileData.userId || currentUser.userId || currentUser.username
        };
        
        // Store user data in localStorage for the timeout handler to access
        localStorage.setItem('auth_user', JSON.stringify(userData));
        setUser(userData);
      } else {
        setUser(prev => ({ 
          ...prev, 
          isLoading: false, 
          isAuthenticated: false,
          role: null,
          name: 'Account User',
          email: 'user@example.com'
        }));
        localStorage.removeItem('auth_user');
      }
    } catch (error) {
      // Only log the error if it's not related to no authenticated user
      if (error.name !== 'UserUnAuthenticatedException' && 
          !error.message.includes('No valid authentication token') &&
          !error.message.includes('User is not authenticated')) {
        console.error('Error loading user data:', error);
      }
      setUser(prev => ({ 
        ...prev, 
        isLoading: false, 
        isAuthenticated: false,
        role: null,
        name: 'Account User',
        email: 'user@example.com'
      }));
      localStorage.removeItem('auth_user');
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadUserData();
    
    // Listen for storage changes (including from other tabs or manual removal)
    const handleStorageChange = (e) => {
      if (e.key === 'auth_user' && e.newValue === null) {
        // localStorage was cleared, refresh user data
        console.log('Auth user storage cleared, refreshing user data');
        loadUserData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Set up a periodic check to verify auth state consistency
    // This helps catch cases where Cognito session expires but AuthContext hasn't updated
    // Only run frequent checks when user is authenticated (potential mismatch scenarios)
    let authCheckInterval = null;
    
    if (user.isAuthenticated) {
      authCheckInterval = setInterval(async () => {
        try {
          const session = await fetchAuthSession();
          const hasValidSession = !!(session?.tokens?.idToken && session?.tokens?.accessToken);
          const contextThinks = user.isAuthenticated;
          
          // If there's a mismatch, try session recovery first
          if (hasValidSession !== contextThinks) {
            console.log('Auth state mismatch detected, attempting recovery...', {
              hasValidSession,
              contextThinks
            });
            
            // Try to recover the session before refreshing user data
            const recovered = await recoverSession();
            if (recovered) {
              console.log('Session recovered, refreshing user data...');
              loadUserData();
            } else {
              console.log('Session recovery failed, user will need to re-authenticate');
              loadUserData();
            }
          }
        } catch (error) {
          // If we can't check session and context thinks user is authenticated, try recovery
          if (user.isAuthenticated) {
            console.log('Session check failed, attempting recovery...');
            const recovered = await recoverSession();
            if (recovered) {
              console.log('Session recovered after check failure, refreshing user data...');
              loadUserData();
            } else {
              console.log('Session recovery failed after check failure, refreshing user data...');
              loadUserData();
            }
          }
        }
      }, 30000); // Check every 30 seconds when authenticated
    }
    
    // Clean up localStorage on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (authCheckInterval) {
        clearInterval(authCheckInterval);
      }
      // Keep auth_user in localStorage for persistence
    };
  }, [loadUserData, user.isAuthenticated]);

  // Provide method to refresh user data
  const refreshUserData = useCallback(() => {
    return loadUserData();
  }, [loadUserData]);

  const contextValue = {
    ...user,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Wrapper component that provides router context
export const AuthProviderWithRouterWrapper = ({ children }) => {
  return (
    <AuthProviderWithRouter>
      {children}
    </AuthProviderWithRouter>
  );
};

// Custom hooks to use the contexts
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthActions = () => {
  const context = useContext(AuthActionsContext);
  if (context === null) {
    throw new Error('useAuthActions must be used within an AuthProviderWithRouter');
  }
  return context;
}; 